import { Label, PanelSection } from '@dev-spendesk/grapes';

import { useTranslation } from 'common/hooks/useTranslation';
import { type Payable } from 'modules/bookkeep/prepare-payables/models/payable';
import { SeeMore } from 'modules/requests/expense/expense-form/SeeMore';
import { getDisplayNameRegisteredBeneficiary } from 'modules/requests/models/beneficiaries';
import { UserAvatar } from 'modules/users/components';
import { UnexpectedErrorCallout } from 'src/core/common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';

import { type PayableType } from '../../models/payable';

type Props = {
  payable: {
    type: PayableType;
    expenseBeneficiaries?: Payable['expenseBeneficiaries'];
  };
  withTitle?: boolean;
};

export const PayableBeneficiariesSection = ({ payable, withTitle }: Props) => {
  const { t } = useTranslation('global');

  const expenseBeneficiaries = payable.expenseBeneficiaries ?? [];

  if (!['claimedBill', 'cardPurchase'].includes(payable.type)) {
    return null;
  }

  const title = t('requests.panel.beneficiariesLabel', {
    count: expenseBeneficiaries.length,
  });

  return (
    <ErrorBoundary
      context={{
        team: 'travel-and-expenses',
        scope: 'payable::panel::expenseBeneficiaries',
      }}
      fallbackComponent={
        <PanelSection title={withTitle ? title : undefined}>
          {!withTitle ? <Label label={title} className="mb-4" /> : undefined}
          <UnexpectedErrorCallout />
        </PanelSection>
      }
    >
      <PanelSection title={withTitle ? title : undefined}>
        {title && expenseBeneficiaries.length === 0 && (
          <div className="-mt-16" />
        )}

        {!withTitle ? <Label label={title} className="mb-4" /> : undefined}

        <SeeMore>
          {expenseBeneficiaries.map((beneficiary) => {
            if (!beneficiary) {
              return null;
            }

            return (
              <div key={beneficiary.id} className="flex items-center gap-8">
                <UserAvatar
                  name={
                    beneficiary.type === 'registeredUser'
                      ? getDisplayNameRegisteredBeneficiary(beneficiary)
                      : beneficiary.unregisteredUser.name
                  }
                  id={
                    beneficiary.type === 'registeredUser'
                      ? beneficiary.registeredUser.id
                      : ''
                  }
                  avatar={
                    beneficiary.type === 'registeredUser'
                      ? beneficiary.registeredUser.avatarUrl
                      : undefined
                  }
                  size={32}
                />
                <span className="truncate">
                  {beneficiary.type === 'registeredUser' &&
                    getDisplayNameRegisteredBeneficiary(beneficiary)}
                  {beneficiary.type === 'unregisteredUser' &&
                    `${beneficiary.unregisteredUser.name} (${beneficiary.unregisteredUser.companyName})`}
                </span>
              </div>
            );
          })}
        </SeeMore>
      </PanelSection>
    </ErrorBoundary>
  );
};
